// UTM / first-touch attribution — the browser half.
//
// The visitor is anonymous when they arrive and stays anonymous through the part
// of the journey that carries the campaign. The account appears later, sometimes
// much later: sign up, then confirm the email, which frequently opens a NEW tab
// on a fresh session. sessionStorage — where this used to live, back when the
// only reader was the waitlist form — loses the record at exactly that step.
//
// So first touch is kept in localStorage. Not a cookie: innernet ships with no
// tracking cookies and no cookie banner (see /privacy and PostHog's
// `persistence: 'localStorage'`), and attribution is not worth breaking that for.
//
// First touch wins, always. Once a record exists it is never overwritten, so
// someone who arrives from a campaign and signs up three days later off a
// bookmark still credits the campaign. The server enforces the same rule again
// on write, because localStorage is the visitor's to edit.
//
// ONE exception, the client twin of the server's weak-row upgrade (2026-10-22).
// A record that says NOTHING (no campaign, no referrer: someone typed the
// address once, weeks ago) is not a first touch, it is an empty one. When a
// visit that does say something arrives before the account exists (a creator's
// DM link, a search, a post), it takes the empty record's place. Found testing
// the codecroc link in a browser that had opened /login on 10 sep: the reel's
// tags were dropped and the signup would have read "direct".

import { templatePlacePath } from "./analytics/placePath";
import {
  sanitizeFirstTouch,
  withInAppReferrer,
  hasSignal,
  stripQuery,
  domainOf,
  UTM_KEYS,
  type Arrival,
  type FirstTouch,
} from "./attribution";

export { UTM_KEYS };
export type { FirstTouch };
export type Utm = Partial<Record<(typeof UTM_KEYS)[number], string>>;

const STORE = "innernet.first_touch";
/** Set once the record is safely on the account — stops every later page load
 *  from re-POSTing it. */
const SENT = "innernet.first_touch.sent";

/** Read the live URL as a first touch, for the visitor we have nothing on yet. */
function fromLocation(): FirstTouch {
  const sp = new URLSearchParams(window.location.search);
  const raw: Record<string, unknown> = { first_seen_at: new Date().toISOString() };
  for (const k of UTM_KEYS) {
    const v = sp.get(k);
    if (v) raw[k] = v;
  }
  // document.referrer is empty for a direct visit and for same-site navigation,
  // which is what makes it the right signal here: it is only ever the page that
  // sent them to us in the first place.
  const ref = document.referrer;
  if (ref && domainOf(ref) !== window.location.hostname.replace(/^www\./, "")) {
    raw.referrer = ref;
    raw.referrer_domain = domainOf(ref);
  }
  // a place's address names the place: keep its shape, never its words
  const landing = stripQuery(window.location.pathname);
  raw.landing_path = landing ? templatePlacePath(landing) : landing;
  // inside Instagram's (or TikTok's, or Facebook's) own browser the referrer is
  // blank, and the app itself is where they came from (lib/attribution)
  return withInAppReferrer(sanitizeFirstTouch(raw), navigator.userAgent, [
    window.location.hostname,
    "innernet.live",
  ]);
}

/**
 * Record where this visitor came from, if we haven't already. Safe (and cheap)
 * to call on every mount — after the first landing it reads one localStorage key
 * and returns. Returns the stored record, or null when storage is unavailable.
 */
export function captureFirstTouch(): FirstTouch | null {
  if (typeof window === "undefined") return null;
  try {
    const existing = localStorage.getItem(STORE);
    if (existing) {
      const kept = JSON.parse(existing) as FirstTouch;
      // first-touch — don't overwrite, unless what is kept says nothing, this
      // visit says something, and no account has the record yet
      if (hasSignal(sanitizeFirstTouch(kept)) || localStorage.getItem(SENT)) return kept;
      const now = fromLocation();
      if (!hasSignal(now)) return kept;
      // the door a consent page marked is about the account, not the visit: keep it
      const next = kept.arrival ? sanitizeFirstTouch({ ...now, arrival: kept.arrival, arrival_client: kept.arrival_client }) : now;
      localStorage.setItem(STORE, JSON.stringify(next));
      return next;
    }
    const ft = fromLocation();
    localStorage.setItem(STORE, JSON.stringify(ft));
    return ft;
  } catch {
    // Private mode / storage disabled. Attribution degrades to whatever the URL
    // still says at read time; nothing user-facing breaks.
    return null;
  }
}

/** Historic name, kept because it reads well at a call site: grab the ?utm_*. */
export const captureUtm = captureFirstTouch;

/**
 * Say which door this person is coming through, from the door itself.
 *
 * Unlike the first touch, this MAY overwrite what is stored: first touch is
 * where the browser was first seen (weeks ago, possibly), while arrival is
 * about the account being made right now, and the consent page knows something
 * no earlier page load could. It rides the same single write, so recording it
 * costs no extra request and cannot produce a second row.
 *
 * It is a no-op once the record has been sent — an existing person revisiting
 * the consent page to connect a second tool must never restate how they first
 * arrived. (The row is write-once server-side too, so this is belt and braces.)
 */
export function markArrival(arrival: Arrival, client?: string): void {
  if (typeof window === "undefined") return;
  try {
    if (localStorage.getItem(SENT)) return; // already on an account — not ours to rewrite
    const base = captureFirstTouch() ?? {};
    const next = sanitizeFirstTouch({ ...base, arrival, arrival_client: client });
    localStorage.setItem(STORE, JSON.stringify(next));
  } catch {
    /* storage disabled — arrival degrades to the console's inference */
  }
}

/** The stored first touch, falling back to the current URL if nothing is kept. */
export function readFirstTouch(): FirstTouch {
  if (typeof window === "undefined") return {};
  try {
    const s = localStorage.getItem(STORE);
    if (s) return sanitizeFirstTouch(JSON.parse(s));
  } catch {
    /* ignore — fall through to the URL */
  }
  try {
    return fromLocation();
  } catch {
    return {};
  }
}

/** Just the campaign fields (what the waitlist form and `track()` want). */
export function readUtm(): Utm {
  const ft = readFirstTouch();
  const out: Utm = {};
  for (const k of UTM_KEYS) {
    const v = ft[k];
    if (v) out[k] = v;
  }
  return out;
}

/**
 * Attach the first touch to whoever is signed in now, once and only once.
 *
 * Designed to be fired from anywhere that runs on an authenticated page — it is
 * a no-op after it has succeeded, and a no-op while nobody is signed in (the
 * route answers 401 and we keep the record for the next load). That makes it
 * self-healing: whatever page the new account lands on first closes the loop,
 * whether that is the redirect after signup, the email-confirm round trip, or a
 * visit tomorrow.
 */
export async function sendFirstTouch(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    if (localStorage.getItem(SENT)) return;
  } catch {
    return; // no storage means no once-only guarantee — don't POST on every load
  }

  const ft = captureFirstTouch() ?? readFirstTouch();

  try {
    const res = await fetch("/api/attribution", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(ft),
      // SURVIVES THE HOP. The session first appears on the apex (/auth/land,
      // /login) a heartbeat before the page navigates to the dashboard host,
      // and a plain fetch is cancelled with the page. Without this the real
      // first touch (the one with the referrer) lost the race to the dashboard
      // origin's recomputed one nearly every time.
      keepalive: true,
    });
    // 401 = still anonymous; leave the record alone and try again next time.
    // Anything else settled (recorded, or already recorded by an earlier
    // session) means this browser is done asking.
    if (res.ok) localStorage.setItem(SENT, "1");
  } catch {
    /* offline or blocked — the record survives, the next load retries */
  }
}
